<template>
    <base-dialog :loading="loading" :value="dialog" :title="title" :width="440" @input="onInput" @close="hide">
        <div v-if="lockedClient" class="payment-client">
            <span class="payment-client-name">{{ lockedClient.name }}</span>
            <span class="payment-client-period">{{ periodLabel }}</span>
        </div>

        <template v-else>
            <div class="form-group">
                <span class="form-label">{{ $t('business.client') }}</span>
                <kimsa-combobox :placeholder="$t('common.select_placeholder')" :items="clientOptions" :value="clientId"
                    item-text="name" item-value="id" no-object dense solo searchable @change="onChangeClient" />
            </div>

            <div class="form-group mt-4">
                <span class="form-label">{{ $t('business.period') }}</span>
                <kimsa-date-picker type="month" :value="period" @change="period = $event" solo flat dense
                    color="secondary" force-value />
            </div>
        </template>

        <div class="form-row mt-4">
            <div class="form-group">
                <span class="form-label">{{ $t('business.amount') }}</span>
                <kimsa-text-field placeholder="80" :value="amount" @change="amount = $event" input-mode="numeric" dense
                    force-value />
            </div>
            <div class="form-group">
                <span class="form-label">{{ $t('business.method') }}</span>
                <kimsa-combobox :placeholder="$t('common.select_placeholder')" :items="methodOptions" :value="method"
                    item-text="name" item-value="value" no-object dense solo clearable @change="method = $event" />
            </div>
        </div>

        <div class="form-group mt-4">
            <span class="form-label">{{ $t('business.payment_date') }}</span>
            <kimsa-date-picker :value="date" @change="date = $event" solo flat dense color="secondary" force-value />
        </div>

        <span v-if="advanceLabel" class="payment-hint">{{ advanceLabel }}</span>

        <template v-slot:footer>
            <dialog-actions :confirm-label="$t('business.register_payment')" :disabled="!valid" :loading="loading"
                @cancel="hide" @confirm="save">
                <template v-slot:extra>
                    <button v-if="isEdit" type="button" class="payment-delete" @click="remove">
                        {{ $t('common.delete') }}
                    </button>
                </template>
            </dialog-actions>
        </template>
        <kimsa-confirm ref="deleteConfirm" :title="$t('business.delete_payment')"
            :content="$t('business.delete_payment_body')" :persistent="false" alert-mode show-cancel
            @confirm="onConfirmDelete" />
    </base-dialog>
</template>

<script>
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaDatePicker from "@/components/common/inputs/KimsaDatePicker";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import { PAYMENT_METHODS, nextPaymentAfter, parseAmount, today, toDateInput } from "@/utils/Business";

export default {
    name: "PaymentDialog",
    components: { BaseDialog, DialogActions, KimsaTextField, KimsaCombobox, KimsaDatePicker, KimsaConfirm },
    props: {
        clients: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            dialog: false,
            payment: null,
            lockedClient: null,
            clientId: null,
            period: '',
            amount: '',
            method: '',
            date: '',
        }
    },
    computed: {
        isEdit(vm = this) {
            return !!vm.payment?.id
        },
        title(vm = this) {
            return vm.isEdit ? vm.$t('business.edit_payment') : vm.$t('business.new_payment')
        },
        clientOptions(vm = this) {
            return vm.clients.filter(client => client.id || client.studentId)
        },
        methodOptions(vm = this) {
            void vm.$i18n.locale
            return PAYMENT_METHODS.map(value => ({ value, name: vm.$t(`business.method_${value}`) }))
        },
        periodLabel(vm = this) {
            void vm.$i18n.locale
            return vm.period ? vm.$moment(vm.period, 'YYYY-MM').format('MMMM YYYY') : ''
        },
        selected(vm = this) {
            return vm.lockedClient || vm.clients.find(client => client.id === vm.clientId) || null
        },
        advanceLabel(vm = this) {
            void vm.$i18n.locale
            if (!vm.selected || !vm.period) return ''
            const next = nextPaymentAfter(vm.selected, vm.period, vm.date)
            if (!next) return ''
            return vm.$t('business.next_payment_moves', {
                date: vm.$moment(next, 'YYYY-MM-DD').format('DD/MM/YYYY'),
            })
        },
        valid(vm = this) {
            return !!vm.selected && /^\d{4}-\d{2}$/.test(vm.period) && parseAmount(vm.amount) > 0
        },
    },
    methods: {
        show({ client = null, period = '', payment = null } = {}) {
            const vm = this
            vm.payment = payment
            vm.lockedClient = client
            vm.clientId = client?.id || null
            vm.period = payment?.period || period || vm.$moment().format('YYYY-MM')
            vm.amount = payment?.amount || client?.planAmount || ''
            vm.method = payment?.method || client?.method || ''
            vm.date = toDateInput(payment?.date) || today()
            vm.dialog = true
        },
        hide() {
            this.dialog = false
        },
        onInput(value) {
            if (!value) this.hide()
        },
        onChangeClient(clientId) {
            const vm = this
            vm.clientId = clientId
            const client = vm.clients.find(item => item.id === clientId)
            if (client && !parseAmount(vm.amount)) vm.amount = client.planAmount || ''
            if (client && !vm.method) vm.method = client.method || ''
        },
        save() {
            const vm = this
            if (!vm.valid) return
            vm.$emit('save', {
                id: vm.payment?.id || null,
                client: vm.selected,
                period: vm.period,
                amount: parseAmount(vm.amount),
                method: vm.method,
                date: vm.date,
            })
        },
        remove() {
            this.$refs.deleteConfirm.show()
        },
        onConfirmDelete() {
            this.$emit('delete', this.payment)
        },
    },
}
</script>

<style scoped>
.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.form-label {
    font-size: var(--fs-label);
    font-weight: 500;
    color: var(--v-txtSecondary-base);
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.payment-client {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.payment-client-name {
    font-size: var(--fs-body);
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.payment-client-period {
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
    text-transform: capitalize;
}

.payment-hint {
    display: block;
    margin-top: 14px;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.payment-delete {
    padding: 0 20px;
    height: 52px;
    border-right: 1px solid var(--v-outline-base);
    font-size: var(--fs-body);
    font-weight: 500;
    color: var(--v-error-base);
}
</style>
